import { useState, type JSX } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * True when the server did not give this role the real key: it sets
 * `activation_key_redacted: true` and sends only a `****abcd` tail (or omits
 * the field). Support and Read-only never receive a key; Owner/Admin do.
 */
export function isKeyRedacted(value: string | null | undefined, redacted?: boolean): boolean {
  return redacted === true || !value || value.startsWith('****');
}

/**
 * A sensitive value (activation key) that is masked until the operator
 * asks to see it (shoulder-surfing / screen-share hygiene), and that shows
 * "Hidden for your role" when the license-server withheld the key. Access
 * control is the server's job: for Support and Read-only the key is never in
 * the response, so there is nothing to reveal and no Show button.
 */
export function SecretValue({ value, redacted, label = 'activation key' }: {
  value?: string | null; redacted?: boolean; label?: string;
}): JSX.Element {
  const [shown, setShown] = useState(false);
  if (isKeyRedacted(value, redacted)) {
    const tail = value && value.startsWith('****') && value.length > 4 ? ` (ends ${value.slice(-4)})` : '';
    return (
      <span
        data-testid="secret-hidden"
        title={`Your role cannot view the ${label}. Owner and Admin accounts can.`}
        style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}
      >
        Hidden for your role{tail}
      </span>
    );
  }
  value = value as string;
  const masked = value.length > 8 ? `${value.slice(0, 4)}${'•'.repeat(12)}${value.slice(-2)}` : '•'.repeat(value.length || 8);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }} data-testid="secret-value">
        {shown ? value : masked}
      </code>
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-pressed={shown}
        aria-label={shown ? `Hide ${label}` : `Show ${label}`}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: 4, borderRadius: 6, border: '1px solid var(--border)',
          background: 'var(--bg-overlay)', color: 'var(--text-muted)', cursor: 'pointer',
        }}
      >
        {shown ? <EyeOff width={12} height={12} aria-hidden="true" /> : <Eye width={12} height={12} aria-hidden="true" />}
      </button>
    </span>
  );
}
